<template>
  <div class="xmind-integration-embed-editor h-full w-full" :class="[{ 'is-borderless': !isShowFrame }]" @click="sendMapClickMessage">
    <EditorView v-if="xmindFile" :key="`editor-view-${cacheId}`" :cache-id="cacheId" :file="xmindFile" :file-name="fileName" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { storeToRefs } from 'pinia'

import debounce from 'utils/debounce'
import { generateShortUUID } from 'utils/shortUUID'
import { useMindMap } from 'client/pages/app/fragments/compositions/mindmap'
import { useWorkspaceStore } from 'app/services/pinia/workspace-store'
import type { EditorNextFile } from 'client/types/file'
import { dataKit } from 'compositions/mindmap'

import EditorView from './editor-view.vue'
import { newMakerXmindFile } from './new-map-content'
import { useEmbedEvents } from '../composables/use-embed-events'
import { useGlobalConfig } from '../composables/use-global-config'
import { useMarkdownToMindmap } from './composables/use-markdown-to-mindmap'
import type { GlobalConfigPayload, importMarkdownPayload, LoadEventPayload } from '../types'
import { useEditorHelper } from './composables/use-editor-helper'
import { getContentJSON } from 'server/apps/main-server/src/lib/new-map-content-json'
import { useRoute } from 'vue-router'
import type { Lang } from 'client/library/translation'

const xmindFile = shallowRef<EditorNextFile | ArrayBuffer | null>(null)
const cacheId = ref(generateShortUUID())
const fileName = ref('Untitled.xmind')
const isLoading = ref(true)

const workspaceStore = useWorkspaceStore()
const { editor: editorState, workbook } = storeToRefs(workspaceStore)

const renderTimeoutHandle = ref<number | null>(null)
const isExpectingLoad = ref(false)

const {
  emit: emitEmbedEvent,
  on: onEmbedEvent,
  sendReadyMessage,
  destroy: destroyEmbedEventChannel,
} = useEmbedEvents('xmind-integration-embed-editor')
const globalConfigStore = useGlobalConfig()
const { globalConfig } = storeToRefs(globalConfigStore)
const { applyConfig } = globalConfigStore
const isShowFrame = computed(() => globalConfig.value.isShowFrame)

const sendMapClickMessage = () => emitEmbedEvent('map-click', null)

const clearRenderTimeout = () => {
  if (renderTimeoutHandle.value !== null) {
    clearTimeout(renderTimeoutHandle.value)
    renderTimeoutHandle.value = null
  }
}

const scheduleRenderTimeout = () => {
  clearRenderTimeout()
  if (!globalConfig.value.timeoutDuration) return
  renderTimeoutHandle.value = window.setTimeout(() => {
    if (!isExpectingLoad.value) return
    emitEmbedEvent('timeout', null)
  }, globalConfig.value.timeoutDuration)
}

const destroyEditor = () => {
  try {
    useMindMap()?.dispose?.()
  } catch (error) {
    logger?.warn?.('EmbedEditor destroy editor failed', error)
  }
  if (cacheId.value) {
    workspaceStore.removeEditorState(cacheId.value)
  }
  xmindFile.value = null
  isExpectingLoad.value = false
  isLoading.value = false
  clearRenderTimeout()
}

const sendRenderingDoneMessage = debounce((sheetId: string) => emitEmbedEvent('rendered', sheetId), 100)
const onSheetRendered = (sheetId: string | undefined) => {
  if (!sheetId) return
  clearRenderTimeout()
  isLoading.value = false
  isExpectingLoad.value = false
  sendRenderingDoneMessage(sheetId)
}

watch(
  () => workbook.value?.workbookEditor.activeSheetFigure?.value?.sheetId,
  (sheetId, prevSheetId) => {
    if (!sheetId || sheetId === prevSheetId) return
    onSheetRendered(sheetId)
  },
  { immediate: true },
)

watch(
  () => workbook.value?.document.value,
  debounce(async () => {
    if (isLoading.value) return
    const mapData = (await dataKit.DKserializeDocument(workbook.value?.document.value)) as EditorNextFile
    emitEmbedEvent('map-changed', mapData)
  }, 50),
  { immediate: true },
)

watch(
  () => editorState.value?.mapStatus,
  (status, previousStatus) => {
    if (status === 'opening') {
      isLoading.value = true
    }
    if (status === 'loaded' && previousStatus !== 'loaded') {
      const activeSheetId = workbook.value?.workbookEditor?.activeSheetFigure?.value?.sheetId
      onSheetRendered(activeSheetId)
    }
    if (status === 'unloaded' && previousStatus === 'opening' && isExpectingLoad.value) {
      emitEmbedEvent('load-error', null)
      clearRenderTimeout()
      isExpectingLoad.value = false
      isLoading.value = false
    }
  },
)

const loadFile = debounce(async (payload: LoadEventPayload) => {
  destroyEditor()
  isLoading.value = true
  isExpectingLoad.value = true
  cacheId.value = generateShortUUID()
  fileName.value = 'Untitled.xmind'
  xmindFile.value = payload
  scheduleRenderTimeout()
}, 100)

const route = useRoute()
const loadDemo = debounce(async () => {
  const lang = (route.query['lang'] as Lang) || 'en-US'
  const contentJSON = getContentJSON(lang)

  const document = await dataKit.deserializeDocument({ content: contentJSON })
  const serialized = (await dataKit.serializeDocument(document)) as unknown as ArrayBuffer

  loadFile(serialized)
}, 100)

const loadMakerFile = debounce(async (skeleton: string = 'Mind Map') => {
  const document = await newMakerXmindFile(skeleton)
  const serialized = (await dataKit.serializeDocument(document)) as unknown as ArrayBuffer
  loadFile(serialized)
}, 100)

const handleConfig = (config: GlobalConfigPayload) => {
  const prevShowDemo = globalConfig.value.isShowDemo
  applyConfig(config)
  if (config?.mode === 'maker') {
    loadMakerFile(config.skeleton)
  }
  if (typeof config?.isShowDemo !== 'undefined') {
    if (globalConfig.value.isShowDemo) {
      loadDemo()
    } else if (prevShowDemo && !globalConfig.value.isShowDemo) {
      destroyEditor()
    }
  }
}

type UserInteractionType = 'fit-map' | 'change-sheet' | 'zoom-map'
const handleUserInteraction = (type: UserInteractionType, value?: unknown) => {
  const mindMap = useMindMap()
  if (!mindMap?.executeEditorAction) return
  switch (type) {
    case 'fit-map':
      mindMap.executeEditorAction({ name: 'mutateEditor:fitMap' })
      break
    case 'change-sheet':
      if (typeof value === 'string' && value) {
        mindMap.executeEditorAction({ name: 'mutateEditor:switchSheet', inputValue: value })
      }
      break
    case 'zoom-map': {
      const numericValue = typeof value === 'number' ? value : Number(value)
      if (!Number.isFinite(numericValue)) return
      mindMap.executeEditorAction({ name: 'mutateEditor:zoomScale', inputValue: numericValue / 100 })
      break
    }
    default:
      break
  }
}

const eventUnsubscribes: Array<() => void> = []
eventUnsubscribes.push(onEmbedEvent('load', payload => loadFile(payload)))
eventUnsubscribes.push(onEmbedEvent('config', payload => handleConfig(payload)))
eventUnsubscribes.push(onEmbedEvent('fit-map', value => handleUserInteraction('fit-map', value)))
eventUnsubscribes.push(onEmbedEvent('change-sheet', value => handleUserInteraction('change-sheet', value)))
eventUnsubscribes.push(onEmbedEvent('zoom-map', value => handleUserInteraction('zoom-map', value)))
eventUnsubscribes.push(
  onEmbedEvent('import-markdown', async (payload: importMarkdownPayload) => {
    const { handleConvertMarkdownToXmind, injectContentToDocument } = useMarkdownToMindmap()
    const markdownDoc = await handleConvertMarkdownToXmind(payload.content)
    if (payload.isNewMap || !workbook.value?.document.value) {
      loadFile((await dataKit.serializeDocument(markdownDoc)) as LoadEventPayload)
      payload?.filename && (fileName.value = payload.filename)
    } else {
      const updatedDoc = await injectContentToDocument(markdownDoc, workbook.value.document.value)
      workbook.value.workbookEditor?.changeDoc(updatedDoc)
    }
    emitEmbedEvent('markdown-imported', { filename: payload.filename, id: payload.id })
  }),
)
eventUnsubscribes.push()
onEmbedEvent('archive-file', async () => {
  const { archiveFile } = useEditorHelper()
  if (!workbook.value?.document.value) return
  const archivedFile = await archiveFile(workbook.value?.document.value, workbook?.value?.stage)
  emitEmbedEvent('archive-completed', { xmindFile: archivedFile.xmindFile, thumbnail: archivedFile.thumbnail })
})

onMounted(() => {
  setTimeout(sendReadyMessage, 50)
  if (globalConfig.value.isShowDemo) {
    loadDemo()
  }
})

onBeforeUnmount(() => {
  eventUnsubscribes.forEach(unsub => unsub())
  destroyEmbedEventChannel()
  clearRenderTimeout()
  destroyEditor()
})
</script>

<style lang="less">
.xmind-integration-embed-editor {
  &.no-scrollbar {
    :deep(.sheet-viewer::-webkit-scrollbar) {
      display: none;
    }
  }
}
</style>
